import { t } from "@lingui/macro";
import { CircleNotch, FileText } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

type Props = {
  isLoading: boolean;
};

export const BuilderLoadingOverlay = ({ isLoading }: Props) => {
  // Self-dismissing safety net: if isLoading stays true for 8s, force hide
  // This prevents the overlay from being stuck forever if postMessage communication fails
  const [forceHidden, setForceHidden] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setForceHidden(false);
      return;
    }
    const timer = setTimeout(() => {
      setForceHidden(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const shouldShow = isLoading && !forceHidden;

  // When hidden, return null immediately - don't rely on AnimatePresence exit
  // (AnimatePresence can get stuck with rapid re-renders)
  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="builder-loading-overlay"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col items-center gap-4 rounded-2xl bg-white/95 px-8 py-6 shadow-2xl dark:bg-gray-900/95"
        >
          {/* Icon with spinning loader */}
          <div className="relative">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <FileText className="h-8 w-8 text-primary" weight="duotone" />
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-1"
            >
              <CircleNotch className="h-[72px] w-[72px] text-primary" weight="bold" />
            </motion.div>
          </div>

          {/* Loading text */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              {t`Loading your resume`}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t`Please wait while we prepare your document...`}
            </p>
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: i * 0.15,
                }}
                className="h-2 w-2 rounded-full bg-primary"
              />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
